import React, { useState, useEffect } from 'react';
import { Divider, TextField } from '@mui/material';
import { FilterPanel } from '../../components/FilterPanel';
import { getSlotConfigDayLive } from '../../services/liveService';
import { saveLiveSlot } from '../../services/saveLive';
import EditIcon from '@mui/icons-material/Edit';
import { useLoading } from '../../context/LoadingProvider';
import { useContext } from 'react';
import { AppContext } from '../../layout/MainLayout';
import './LiveSlot.css';

const LivePage = () => {
  const {
    selectedLocation,
    filters,
    setFilters,
    templateData,
    setTemplateData,
  } = useContext(AppContext); const { setLoading } = useLoading();

  const [editIndex, setEditIndex] = useState(null);
  const [editTotalIndex, setEditTotalIndex] = useState(null);

  const [timeValue, setTimeValue] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  useEffect(() => {
    const today = new Date();
    const hours = today.getHours();
    const minutes = today.getMinutes();
    const seconds = today.getSeconds();

    const liveTimestamp = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes,
      seconds
    ).getTime();

    setFilters((prev) => ({
      ...prev,
      toTime: [liveTimestamp],
    }));
  }, [setFilters]);

  const handleTimeChange = (e) => {
    const value = e.target.value;
    setTimeValue(value);

    if (value) {
      const [year, month, day] = value.split('-').map(Number);
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();

      const liveTimestamp = new Date(year, month - 1, day, hours, minutes, seconds).getTime();

      setFilters((prev) => ({
        ...prev,
        toTime: [liveTimestamp],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        toTime: [],
      }));
    }
  };

  const fetchLiveSlotData = async () => {
    const payload = {
      vehicleType: filters.vehicleType?.[0] || '',
      regionId: filters.region?.[0]?.regionId || '',
      toTime: filters.toTime?.[0] || Date.now(),
      corporateId: filters.corporate?.[0] || '',
      tripType: filters.tripType?.[0] || '',
    };

    try {
      setLoading(true);
      setTemplateData([]);
      const data = await getSlotConfigDayLive(payload);
      setTemplateData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch live slot data:', err);
      setTemplateData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    await fetchLiveSlotData();
  };

  const handleClearAll = () => {
    setFilters({});
    setTemplateData([]);
    setTimeValue('');
  };

  const handleSlotEdit = (index, newValue, field = 'slotAvailable') => {
    const updatedData = [...templateData];
    updatedData[index][field] = parseInt(newValue, 10) || 0;
    updatedData[index].isEdited = true;
    setTemplateData(updatedData);
  };

  const handleSave = async () => {
    const editedSlots = templateData.filter((slot) => slot.isEdited);

    if (editedSlots.length === 0) {
      alert('No changes to save.');
      return;
    }

    const payload = editedSlots.map(({ slotId, totalSlot }) => ({
      slotId,
      totalCount: totalSlot,
    }));

    try {
      setLoading(true);
      const response = await saveLiveSlot(payload);
      alert(response === true ? 'Slots saved successfully!' : 'Failed to save slots.');

      const resetData = templateData.map((item) => ({ ...item, isEdited: false }));
      setTemplateData(resetData);
    } catch {
      alert('An error occurred while saving slots.');
    } finally {
      setLoading(false);
    }
  };

  const timeField = (
    <TextField
      label="Date"
      type="date"
      value={timeValue}
      onChange={handleTimeChange}
      size="small"
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 200 }}
    />
  );

  return (
    <div className="live-page">
      <FilterPanel
        filters={filters}
        setFilters={setFilters}
        onApplyFilters={handleApplyFilters}
        onClearAll={handleClearAll}
        selectedLocation={selectedLocation}
        extraFields={timeField}
        excludeKeys={['dayOfWeek']}
      />

      {templateData?.length > 0 && (
        <div className="live-grid">
          {templateData.map(({ slotTime, slotAvailable, slotId, totalSlot }, idx) => (
            <div key={slotId || idx} className="live-card">
              <div className="live-slot-time">Slot Time: {slotTime}</div>
              <Divider style={{ margin: '8px 0' }} />

              {/* Slots Available */}
              <div className="live-slot-available">
                <span className="live-slot-available-label">Slots Available</span>
                <div className="live-slot-divider" />
                {editIndex === idx ? (
                  <TextField
                    type="number"
                    size="small"
                    value={slotAvailable}
                    onChange={(e) => handleSlotEdit(idx, e.target.value, 'slotAvailable')}
                    inputProps={{ style: { fontSize: '16px', width: 60 } }}
                    onBlur={() => setEditIndex(null)}
                    autoFocus
                  />
                ) : (
                  <span style={{ fontWeight: 600, fontSize: '18px', paddingLeft: '48px' }}>
                    {slotAvailable ?? '-'}
                  </span>
                )}
                {/* <EditIcon
                  fontSize="inherit"
                  style={{ marginLeft: 8, cursor: 'pointer', color: 'black', fontSize: 16 }}
                  onClick={() => setEditIndex(idx)}
                /> */}
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* Total Slot */}
              <div className="live-slot-available">
                <span className="live-slot-total-label">Total Slot</span>
                <div className="live-slot-divider" />
                {editTotalIndex === idx ? (
                  <TextField
                    type="number"
                    size="small"
                    value={totalSlot}
                    onChange={(e) => handleSlotEdit(idx, e.target.value, 'totalSlot')}
                    inputProps={{ style: { fontSize: '16px', width: 60 } }}
                    onBlur={() => setEditTotalIndex(null)}
                    autoFocus
                  />
                ) : (
                  <span style={{ fontWeight: 600, fontSize: '18px', paddingLeft: '48px' }}>
                    {totalSlot ?? '-'}
                  </span>
                )}
                <EditIcon
                  fontSize="inherit"
                  style={{ marginLeft: 8, cursor: 'pointer', color: 'black', fontSize: 16 }}
                  onClick={() => setEditTotalIndex(idx)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {(!templateData || templateData.length === 0) && (
        <div className="no-data-container">
          <svg className="no-data-icon" viewBox="0 0 64 64" width="120" height="120" fill="none">
            <circle cx="32" cy="32" r="30" stroke="#ccc" strokeWidth="2" />
            <path d="M20 30h24M20 36h16" stroke="#aaa" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <p className="no-data-text">No slot available</p>
        </div>
      )}

      {templateData?.length > 0 && (
        <div className="live-save-button-container">
          <button className="live-save-btn" onClick={handleSave} disabled={editIndex !== null}>
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default LivePage;